<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import type { CategorySlug } from '~/constants/categories'
import { isValidCategorySlug, CATEGORY_META } from '~/constants/categories'
import type { CategoryFiltersState, Product, CategoryFilter } from '~/types/database'

// Apply category middleware for validation
definePageMeta({
  middleware: 'category'
})

// Get category slug from route (validation handled by middleware)
const route = useRoute()
const categorySlug = computed(() => route.params.category as string)

// Runtime config for site URL
const config = useRuntimeConfig()
const siteUrl = computed(() => config.public.siteUrl || 'https://granados-pinturas.com')

// Composables
const { category: dbCategory, loading: categoryLoading, error: categoryError } = useCategoryPage(categorySlug as Ref<CategorySlug>)
const { filters, setFilters, clearFilters, hasActiveFilters, activeFilterCount } = useCategoryFilters()
const { fetchProducts } = useProductsByCategory()
const { fetchDynamicOptions } = useDynamicFilterOptions()

// Fallback category from static metadata when DB category isn't available
const fallbackCategory = computed(() => {
  if (!isValidCategorySlug(categorySlug.value)) return null
  const meta = CATEGORY_META[categorySlug.value]
  return {
    id: categorySlug.value,
    name: meta.name,
    slug: categorySlug.value,
    description: meta.description,
    hero_title: meta.title,
    hero_description: meta.description,
    hero_image_url: null,
    brand_color: meta.color,
    brand_logo_url: null,
    is_premium_brand: meta.isPremium,
    display_order: 0,
    sort_order: 0,
    meta_title: meta.title,
    meta_description: meta.description,
    og_image_url: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    filters: []
  }
})

// Use DB category if available, otherwise use fallback
const category = computed(() => dbCategory.value || fallbackCategory.value)

// Local state
const searchQuery = ref((route.query.q as string) || '')
const currentPage = ref(1)
const pageSize = ref(24)

// Dynamic filter options state
interface DynamicOption {
  value: string
  label: string
  count: number
}
const dynamicOptions = ref<Record<string, DynamicOption[]>>({})
const optionsLoading = ref(false)

/**
 * Load products with SSR support using useAsyncData
 * Watches dbCategory so it fetches when category becomes available
 */
const { data: productsData, pending: productsLoading, refresh: refreshProducts } = useAsyncData(
  () => `products-${categorySlug.value}-${dbCategory.value?.id || 'loading'}-${currentPage.value}`,
  async () => {
    // Only fetch if we have a real DB category
    if (!dbCategory.value) {
      return { products: [], total: 0 }
    }

    const result = await fetchProducts(
      dbCategory.value.id,
      filters as CategoryFiltersState,
      searchQuery.value,
      currentPage.value,
      pageSize.value
    )

    return result
  },
  {
    // Watch for changes - including dbCategory to trigger refetch when it loads
    watch: [dbCategory, () => filters, searchQuery, currentPage],
    // Default value for SSR
    default: () => ({ products: [], total: 0 })
  }
)

// Computed refs for products (derived from async data)
const products = computed(() => productsData.value?.products || [])
const totalProducts = computed(() => productsData.value?.total || 0)

// Debounced search
const debouncedSearch = useDebounceFn((query: string) => {
  searchQuery.value = query
  currentPage.value = 1
  updateUrlQuery()
}, 300)

/**
 * Load dynamic filter options based on current filter state
 */
const loadDynamicOptions = async () => {
  if (!dbCategory.value?.filters?.length) return

  optionsLoading.value = true
  try {
    const options = await fetchDynamicOptions(
      dbCategory.value.id,
      dbCategory.value.filters,
      filters as CategoryFiltersState
    )
    dynamicOptions.value = options
  } catch (e) {
    console.error('Error loading dynamic options:', e)
  } finally {
    optionsLoading.value = false
  }
}

/**
 * Update URL with search query
 */
const updateUrlQuery = () => {
  const query: Record<string, string | string[] | undefined> = {}
  // Preserve existing query params except 'q'
  Object.entries(route.query).forEach(([key, value]) => {
    if (key !== 'q' && value !== null) {
      query[key] = value as string | string[]
    }
  })
  if (searchQuery.value) {
    query.q = searchQuery.value
  }
  navigateTo({ query }, { replace: true })
}

/**
 * Handle filter changes from the filter component
 */
const handleFiltersChange = (newFilters: CategoryFiltersState) => {
  setFilters(newFilters)
  currentPage.value = 1
  // Reload dynamic options when filters change
  loadDynamicOptions()
  // Products auto-reload via watch in useAsyncData
}

/**
 * Handle individual filter update (for hierarchy reset)
 */
const handleFilterUpdate = async (_key: string, _value: unknown, resetChildren: boolean) => {
  if (resetChildren) {
    // Dynamic options need to be refreshed when parent changes
    await loadDynamicOptions()
  }
}

/**
 * Handle filter clear
 */
const handleClearFilters = () => {
  clearFilters()
  currentPage.value = 1
  // Reload dynamic options with no filters
  loadDynamicOptions()
  // Products auto-reload via watch in useAsyncData
}

/**
 * Handle search
 */
const handleSearch = (query: string) => {
  debouncedSearch(query)
  // Products auto-reload via watch in useAsyncData
}

/**
 * Handle search clear
 */
const handleSearchClear = () => {
  searchQuery.value = ''
  currentPage.value = 1
  updateUrlQuery()
  // Products auto-reload via watch in useAsyncData
}

/**
 * Handle pagination
 */
const handlePageChange = (page: number) => {
  currentPage.value = page
  // Products auto-reload via watch in useAsyncData
  // Scroll to top of products (client-side only)
  if (import.meta.client) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

// Computed values
const totalPages = computed(() => Math.ceil(totalProducts.value / pageSize.value))

const isLoading = computed(() => categoryLoading.value || productsLoading.value)

const showEmptyState = computed(() =>
  !isLoading.value && products.value.length === 0
)

const categoryMeta = computed(() => {
  if (isValidCategorySlug(categorySlug.value)) {
    return CATEGORY_META[categorySlug.value]
  }
  return null
})

// Load dynamic options when DB category is ready
// Products auto-load via useAsyncData watch
watch(dbCategory, (cat) => {
  if (cat) {
    loadDynamicOptions()
  }
}, { immediate: true })

// SEO Meta
useSeoMeta({
  title: () => category.value?.meta_title || category.value?.name || categoryMeta.value?.title || 'Categoría',
  description: () => category.value?.meta_description || category.value?.hero_description || categoryMeta.value?.description,
  ogTitle: () => category.value?.meta_title || category.value?.name,
  ogDescription: () => category.value?.meta_description || category.value?.hero_description,
  ogImage: () => category.value?.og_image_url || category.value?.hero_image_url,
  ogType: 'website'
})

// Structured data for SEO
useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: computed(() => JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: category.value?.name || categoryMeta.value?.name,
        description: category.value?.hero_description || categoryMeta.value?.description,
        url: `${siteUrl.value}/${categorySlug.value}`,
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: totalProducts.value,
          itemListElement: products.value.slice(0, 10).map((product: Product, index: number) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'Product',
              name: product.name,
              sku: product.stock_code,
              url: `${siteUrl.value}/catalogo/${product.slug}`
            }
          }))
        }
      }))
    }
  ]
})
</script>

<template>
  <div>
    <!-- Error State -->
    <template v-if="categoryError">
      <UContainer class="py-16 text-center">
        <UIcon
          name="i-heroicons-exclamation-triangle"
          class="w-16 h-16 mx-auto text-red-400 mb-4"
        />
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Error al cargar la categoría
        </h1>
        <p class="text-gray-600 dark:text-gray-300 mb-6">
          {{ categoryError.message }}
        </p>
        <UButton
          color="primary"
          @click="$router.push('/')"
        >
          Volver al inicio
        </UButton>
      </UContainer>
    </template>

    <!-- Main Content -->
    <template v-else>
      <!-- Hero Section -->
      <CatalogCategoryHero
        v-if="category"
        :category="category"
      />

      <!-- Loading Hero Skeleton -->
      <section
        v-else
        class="bg-gradient-to-r from-gray-700 to-gray-800 py-10 md:py-14"
      >
        <UContainer>
          <USkeleton class="h-10 w-64 mb-4" />
          <USkeleton class="h-6 w-96" />
        </UContainer>
      </section>

      <!-- Breadcrumb -->
      <UContainer class="py-4">
        <CatalogCategoryBreadcrumb
          v-if="category"
          :category="category"
        />
        <USkeleton v-else class="h-5 w-48" />
      </UContainer>

      <!-- Main Grid -->
      <UContainer class="pb-12">
        <div class="lg:flex lg:gap-8">
          <!-- Filters Sidebar -->
          <aside class="lg:w-64 flex-shrink-0">
            <CatalogCategoryFilters
              v-if="category?.filters && dbCategory"
              :filters="category.filters"
              :model-value="filters as CategoryFiltersState"
              :has-active-filters="hasActiveFilters"
              :category-id="dbCategory.id"
              :dynamic-options="dynamicOptions"
              :loading-options="optionsLoading"
              @update:model-value="handleFiltersChange"
              @update:filter="handleFilterUpdate"
              @clear="handleClearFilters"
            />

            <!-- Filters Loading Skeleton -->
            <div v-else-if="categoryLoading" class="hidden lg:block space-y-4">
              <USkeleton class="h-6 w-24" />
              <USkeleton class="h-10 w-full" />
              <USkeleton class="h-10 w-full" />
              <USkeleton class="h-10 w-full" />
            </div>
          </aside>

          <!-- Products Section -->
          <div class="flex-1 min-w-0">
            <!-- Search & Results Header -->
            <div class="mb-6 space-y-4">
              <!-- Search Bar -->
              <div class="relative">
                <UInput
                  :model-value="searchQuery"
                  placeholder="Buscar en esta categoría..."
                  icon="i-heroicons-magnifying-glass"
                  size="lg"
                  class="w-full"
                  @update:model-value="handleSearch"
                >
                  <template v-if="searchQuery" #trailing>
                    <UButton
                      variant="ghost"
                      color="neutral"
                      size="xs"
                      icon="i-heroicons-x-mark"
                      aria-label="Limpiar búsqueda"
                      @click="handleSearchClear"
                    />
                  </template>
                </UInput>
              </div>

              <!-- Results Count & Active Filters -->
              <div class="flex items-center justify-between flex-wrap gap-2">
                <div class="text-sm text-gray-600">
                  <template v-if="productsLoading">
                    <USkeleton class="h-5 w-32 inline-block" />
                  </template>
                  <template v-else>
                    <span class="font-semibold text-gray-900">{{ totalProducts }}</span>
                    {{ totalProducts === 1 ? 'producto' : 'productos' }}
                    <template v-if="searchQuery">
                      para "<span class="font-medium">{{ searchQuery }}</span>"
                    </template>
                  </template>
                </div>

                <!-- Active Filters Badge (Desktop) -->
                <div v-if="hasActiveFilters" class="hidden lg:flex items-center gap-2">
                  <UBadge color="primary" variant="soft">
                    {{ activeFilterCount }} {{ activeFilterCount === 1 ? 'filtro activo' : 'filtros activos' }}
                  </UBadge>
                  <UButton
                    variant="ghost"
                    color="neutral"
                    size="xs"
                    @click="handleClearFilters"
                  >
                    Limpiar todo
                  </UButton>
                </div>
              </div>
            </div>

            <!-- Products Grid -->
            <CatalogProductGrid
              :products="products"
              :loading="productsLoading"
            />

            <!-- Pagination -->
            <div
              v-if="totalPages > 1 && !productsLoading"
              class="mt-8 flex justify-center"
            >
              <UPagination
                :model-value="currentPage"
                :total="totalProducts"
                :items-per-page="pageSize"
                show-edges
                @update:model-value="handlePageChange"
              />
            </div>

            <!-- Empty State with Suggestions -->
            <div
              v-if="showEmptyState"
              class="text-center py-12"
            >
              <UIcon
                name="i-heroicons-magnifying-glass"
                class="w-16 h-16 mx-auto text-gray-300 mb-4"
              />
              <h3 class="text-lg font-semibold text-gray-900 mb-2">
                No se encontraron productos
              </h3>
              <p class="text-gray-500 mb-6 max-w-md mx-auto">
                <template v-if="searchQuery || hasActiveFilters">
                  Intenta ajustar los filtros o buscar con otros términos.
                </template>
                <template v-else>
                  No hay productos disponibles en esta categoría por el momento.
                </template>
              </p>
              <div class="flex justify-center gap-3">
                <UButton
                  v-if="searchQuery"
                  variant="outline"
                  color="neutral"
                  @click="handleSearchClear"
                >
                  Limpiar búsqueda
                </UButton>
                <UButton
                  v-if="hasActiveFilters"
                  variant="outline"
                  color="neutral"
                  @click="handleClearFilters"
                >
                  Limpiar filtros
                </UButton>
                <UButton
                  color="primary"
                  @click="$router.push('/')"
                >
                  Ver todas las categorías
                </UButton>
              </div>
            </div>
          </div>
        </div>
      </UContainer>
    </template>
  </div>
</template>
